// investments.js
// The Vault's rule book: what counts as an investment, how units roll up into
// positions, and how positions roll up into folders.
//
// WHY THIS IS ITS OWN FILE
// The vault is drawn three times — InvestmentsTabZone (board theme),
// InvestmentsTab2 (classic) and MobileInvestments — and the Live tabs need the
// other half of the same rule to know what to leave out. Five copies of "is this
// an investment" is five chances for the two tabs to disagree about where an
// item lives, which is exactly the bug this rework exists to kill.
//
// THE SPLIT
// An investment is NOT a flip. Before this, `isInvestment` was only a flag that
// gave a row "a second home": it stayed in Live and also appeared in the Vault,
// so the same item was counted on two screens and the user had to remember which
// number meant what. Now the two lists are disjoint:
//
//   Live tabs   held, not sold, not traded, NOT an investment   → isFlipRow
//   Vault       held, not sold, not traded, IS an investment    → isVaultRow
//
// Whole-portfolio figures (Overview, Analytics) deliberately keep using
// getUnsoldRows from helpers.js and so still see both — the capital is yours
// either way, and splitting the top-line totals would just move the confusion.
//
// Both predicates now agree on the third state as well: an item traded away is
// in neither list. See the note on isFlipRow.

// The one import this module takes from helpers.js, and only for the predicate.
// buildVault still has getPrice injected rather than importing it, so the
// pricing side stays stubbable in tests — what is NOT worth stubbing is the
// definition of "you no longer own this", which has to be identical everywhere
// or the tabs disagree about where an item lives all over again.
import { isTradedAway } from './helpers';
// The lend predicate comes from the module that owns the concept rather than
// being rewritten here as `!!row.lend`. One definition per idea is the whole
// reason this file exists.
import { isLent } from './lending';

/** Not yet sold, and actually names an item. */
function isUnsold(row) {
  return !!row?.skin?.value && !(parseFloat(row.sellPrice) > 0);
}

/** Held, in your hands, and flagged as a long-term hold — the Vault's contents. */
export function isVaultRow(row) {
  return isUnsold(row) && !isTradedAway(row) && !isLent(row) && !!row.isInvestment;
}

/**
 * Held and NOT an investment — what the Live tabs show.
 *
 * The exact complement of isVaultRow now, and that is the fix. This used to
 * stop at "no sellPrice", which let a traded-away row through: a trade is not a
 * sale, so the row keeps an empty sellPrice forever and sailed straight into
 * Live while also appearing on the Traded tab as the item it plainly was. Worse
 * than the duplicate card, its buyPrice was counted as live capital at the same
 * time as the rows the trade created were carrying that same basis.
 *
 * Note the AND inside isTradedAway rather than an OR: a row holding a tradeId
 * with nothing received yet is the amber Resolve state, still a holding, and it
 * stays right here where it can be answered.
 *
 * Nothing is hidden by this. The row is the left-hand side of its trade on the
 * Traded tab, and every card in the app opens a detail modal that draws the
 * whole chain — see tradeLineage.js.
 *
 * LENT ITEMS ARE OUT TOO, and for a different reason worth keeping straight.
 * A traded-away item is not yours; a lent one still is. What a lend changes is
 * only where the item physically is, and Live is the list of what is in your
 * hands — an item sitting in a friend's inventory cannot be sold, repriced or
 * sent anywhere from this page, so offering it here alongside things you can
 * act on made the list say something untrue about a third of its own rows.
 * The Loan Board is where a lent item lives until it comes back, and it comes
 * back on its own: returnRow deletes `row.lend`, and the item reappears here
 * with nothing else about it changed.
 *
 * This is deliberately NOT mirrored in getUnsoldRows. The money is still yours
 * while the item is out, so every whole-portfolio total goes on counting it —
 * see the note there. Only the two "where does this item live" lists move.
 */
export function isFlipRow(row) {
  return isUnsold(row) && !isTradedAway(row) && !isLent(row) && !row.isInvestment;
}

// ── Folders ────────────────────────────────────────────────
// A folder is a name the user gave a pile of units ("Knives", "Q4 exit"). It is
// stored in settings, and each unit carries its folder's id. Flat and single
// membership on purpose: a unit in two folders would be counted twice in the
// roll-up, and there is no honest way to show that.
//
// Assignment is per UNIT, not per position, so two Karambits bought for
// different reasons can sit in different folders even though they are the same
// skin and condition — which is why the position key below includes the folder.

/** The bucket for units that have not been filed. Never a real folder id. */
export const UNFILED = '__unfiled__';

// Deliberately not the Zone theme's palette: this module is theme-neutral and
// these ids get stored on the user's settings, so they have to keep meaning
// something if the theme changes underneath them.
export const FOLDER_COLORS = [
  '#ffd43d', '#6fe8cf', '#ff9bd2', '#b79cff',
  '#c9f65a', '#ffab5e', '#7dd3fc', '#fda4af',
];

export function getFolders(settings) {
  return Array.isArray(settings?.investmentFolders) ? settings.investmentFolders : [];
}

/**
 * A new folder, coloured by how many already exist so two in a row differ.
 *
 * `extra` carries the optional `setId` — set when the folder stands for an
 * in-game set ("The Huntsman Collection", "EMS Katowice 2014 Legends") rather
 * than a name the user invented. It is what stops a second Huntsman folder
 * appearing the next time something from that set is filed; see folderForSet
 * below. The folder is an ordinary one otherwise: it can be renamed, and its
 * members are not checked against the set afterwards.
 *
 * It also carries the optional `image` and `color` the folder editor picked, so
 * a folder made from that modal arrives already dressed rather than flashing a
 * default colour and then changing.
 */
export function makeFolder(name, existing = [], extra = null) {
  return {
    id: `fold_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    name: String(name || '').trim() || 'Untitled',
    color: extra?.color || FOLDER_COLORS[existing.length % FOLDER_COLORS.length],
    createdAt: new Date().toISOString(),
    ...(extra?.setId ? { setId: extra.setId } : {}),
    ...(extra?.image ? { image: extra.image } : {}),
  };
}

/**
 * The picture to draw a folder with, or null for the colour chip.
 *
 * A picture the user chose wins over the set's own icon: a folder that started
 * life as "EMS Katowice 2014 Legends" and was then given the King on the Field
 * sticker was given it on purpose, and quietly preferring the capsule icon
 * would read as the choice not having been saved.
 */
export function folderImage(folder, set = null) {
  return folder?.image || set?.image || null;
}

/**
 * The folder already standing for an in-game set, or null.
 *
 * Matches on the id first and the name second: folders made before this existed
 * carry no id, and a user who typed "The Huntsman Collection" by hand meant
 * that folder, not a duplicate of it.
 */
export function folderForSet(folders, set) {
  if (!set?.id) return null;
  const list = Array.isArray(folders) ? folders : [];
  return list.find(f => f.setId === set.id)
    || list.find(f => f.name?.trim().toLowerCase() === set.name?.trim().toLowerCase())
    || null;
}

/**
 * Turn a FolderPickModal answer into the id to file under, creating the folder
 * if the answer asked for one. Shared by the three places that open the picker
 * so "reuse the set's folder" is decided once.
 *
 * @param pick        {{ folderId, newFolderName, set }} from the modal
 * @param folders     the folders that exist now
 * @param createFolder (name, extra) => new folder's id
 * @returns folder id, or null for the unfiled pile
 */
export function applyFolderPick(pick, folders, createFolder) {
  if (pick?.set) {
    const existing = folderForSet(folders, pick.set);
    return existing
      ? existing.id
      : createFolder?.(pick.set.name, { setId: pick.set.id }) || null;
  }
  if (pick?.newFolderName) return createFolder?.(pick.newFolderName) || null;
  return pick?.folderId || null;
}

/** The folder a row is filed under, or UNFILED. Unknown ids read as unfiled so
 *  deleting a folder never hides the units that were in it. */
export function folderOf(row, folders) {
  const id = row?.investmentFolder;
  if (!id) return UNFILED;
  return folders.some(f => f.id === id) ? id : UNFILED;
}

// ── Roll-up ────────────────────────────────────────────────

const dayCount = (ms) => Math.max(0, Math.floor((Date.now() - ms) / 86400000));

/** Units → one position. Money is summed; the oldest buy dates the position. */
function summarise(units, price) {
  const cost = units.reduce((s, u) => s + u.buy, 0);
  const qty  = units.length;
  const value = price != null ? price * qty : cost;
  const pl    = price != null ? value - cost : null;
  return {
    qty, cost, value, pl,
    avg: qty ? cost / qty : 0,
    plPct: pl != null && cost > 0 ? (pl / cost) * 100 : null,
  };
}

/**
 * Everything the Vault draws, in one pass.
 *
 * @param {object}   input
 * @param {Array}    input.rows        every tracker row, in array order
 * @param {object}   input.livePrices  Buff quotes, keyed by market name
 * @param {object}   input.settings    carries the folder list
 * @param {Function} input.getPrice    (livePrices, row) → quote; injected so this
 *                                     module does not depend on helpers.js and
 *                                     can be tested with a stub
 * @returns {{ groups: Array, positions: Array, totals: object }}
 */
export function buildVault({ rows = [], livePrices, settings, getPrice }) {
  const folders = getFolders(settings);

  // realIndex is the row's position in the unfiltered array — the only handle
  // the rest of the app accepts for editing or selling a specific unit.
  const units = [];
  (rows || []).forEach((row, realIndex) => {
    if (!isVaultRow(row)) return;
    const t = +new Date(row.timestamp);
    units.push({
      row, realIndex,
      buy: parseFloat(row.buyPrice) || 0,
      float: row.float ?? null,
      paintSeed: row.paintSeed ?? null,
      bought: Number.isFinite(t) ? t : Date.now(),
      folderId: folderOf(row, folders),
    });
  });

  // Folder first, so a position never straddles two folders and every group's
  // total is just the sum of the positions drawn inside it.
  const byPosition = new Map();
  for (const u of units) {
    const skin = u.row.skin.value;
    const cond = u.row.condition?.value || '';
    const key  = `${u.folderId}|${skin}|${cond}`;
    let pos = byPosition.get(key);
    if (!pos) {
      pos = { key, folderId: u.folderId, skin, cond, row: u.row, units: [] };
      byPosition.set(key, pos);
    }
    pos.units.push(u);
  }

  const positions = [...byPosition.values()].map(pos => {
    const price  = getPrice(livePrices, pos.row)?.starting_at?.price ?? null;
    const oldest = Math.min(...pos.units.map(u => u.bought));
    // Newest buy first: the drill-down is usually opened to act on something
    // recent, and the oldest unit is already named by the position's age.
    const list = [...pos.units].sort((a, b) => b.bought - a.bought).map(u => ({
      ...u,
      days:  dayCount(u.bought),
      pl:    price != null ? price - u.buy : null,
      plPct: price != null && u.buy > 0 ? ((price - u.buy) / u.buy) * 100 : null,
    }));
    return {
      ...pos, units: list, price, oldest,
      days: dayCount(oldest),
      ...summarise(pos.units, price),
    };
  });

  // Named folders in the order the user made them, then the unfiled pile. Empty
  // folders are kept: a folder you just made and have not filled yet has to be
  // visible or there is nowhere to drop anything.
  // setId and image ride along so a folder can be drawn with the set's icon or
  // with the picture the user picked. Listed explicitly rather than spreading
  // `f`, to keep the group a known shape rather than whatever a folder happens
  // to carry.
  const groups = folders.map(f => ({
    id: f.id, name: f.name, color: f.color, setId: f.setId || null,
    image: f.image || null, unfiled: false,
    positions: positions.filter(p => p.folderId === f.id),
  }));
  const loose = positions.filter(p => p.folderId === UNFILED);
  if (loose.length) {
    groups.push({ id: UNFILED, name: 'Unfiled', color: null, image: null, unfiled: true, positions: loose });
  }

  const roll = (list) => {
    const cost  = list.reduce((s, p) => s + p.cost, 0);
    const value = list.reduce((s, p) => s + p.value, 0);
    const qty   = list.reduce((s, p) => s + p.qty, 0);
    const pl    = value - cost;
    return {
      cost, value, pl, units: qty,
      // `count`, not `positions`: a group carries its own `positions` ARRAY, and
      // spreading a roll-up over it would replace the list the caller is about
      // to render with a number.
      count: list.length,
      // Priced against cost, not against value: an unpriced position falls back
      // to its cost above, so its contribution to `pl` is zero either way.
      plPct: cost > 0 ? (pl / cost) * 100 : null,
      oldestDays: list.reduce((d, p) => Math.max(d, p.days), 0),
      // How much of this roll-up is a real market reading rather than a fallback
      // to cost — the difference between "flat" and "unknown".
      priced: list.filter(p => p.price != null).length,
    };
  };

  return {
    groups: groups.map(g => ({ ...g, ...roll(g.positions) })),
    positions,
    totals: roll(positions),
  };
}
